import React, { useState, useMemo, useCallback } from "react";
import {
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Clock,
  AlertCircle,
  Activity,
  FileText,
  ArrowUpDown,
  Eye,
  Package,
  ListChecks,
  Users,
  Calendar,
  Target,
  TrendingUp,
} from "lucide-react";
import "./TableView.scss";
import { EnquiryTimelineIcon } from "../../pages/Enquiries/Enquirytimelinesidebar";

const TableView = ({
  data = [],
  onToggleDetails,
  selectedItem,
  onStageProgression,
  onMoveToImmediateNext,
  onProductEdit,
  onProductClick,
  onOpenModal,
  onQuickComplete,
  onOpenBOM,
  onOpenMOM,
  canMoveToNextStage,
  getStatusIcon,
  getStageTypeIcon,
  isChecklistComplete,
  formatDateTime,
  loadingItems,
  isTeamView,
  onAddProducts,
  getApiEndpoint,
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const pageSizeOptions = [10, 25, 50, 100];

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;

    const term = searchTerm.toLowerCase();

    const deepSearch = (value) => {
      if (value == null) return false;

      if (typeof value === "string" || typeof value === "number") {
        return String(value).toLowerCase().includes(term);
      }

      if (Array.isArray(value)) {
        return value.some(deepSearch);
      }

      if (typeof value === "object") {
        return Object.values(value).some(deepSearch);
      }

      return false;
    };

    return data.filter((item) => deepSearch(item));
  }, [data, searchTerm]);

  // Sorting logic - FIXED: Changed dependency from 'data' to 'filteredData'
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle nested values
      if (sortConfig.key === "stageName") {
        aValue = a.stageName || "";
        bValue = b.stageName || "";
      } else if (sortConfig.key === "progress") {
        aValue = a.progress || 0;
        bValue = b.progress || 0;
      } else if (sortConfig.key === "createdAt") {
        aValue = new Date(a.createdAt || 0);
        bValue = new Date(b.createdAt || 0);
      } else if (sortConfig.key === "clientName") {
        aValue = a.leadName || a.clientName || "";
        bValue = b.leadName || b.clientName || "";
      } else if (sortConfig.key === "productCount") {
        aValue = a.productHistories?.length || 0;
        bValue = b.productHistories?.length || 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [filteredData, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  const paginatedData = sortedData.slice(startIndex, endIndex);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const goToPreviousPage = () => setCurrentPage((p) => Math.max(1, p - 1));

  const goToNextPage = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  const toggleRowExpansion = useCallback((itemId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  const SortableHeader = ({ label, sortKey, icon: Icon }) => (
    <th onClick={() => handleSort(sortKey)} className="sortable-header">
      <div className="header-content">
        {Icon && <Icon size={16} className="header-icon" />}
        <span>{label}</span>
        <ArrowUpDown
          size={14}
          className={`sort-icon ${sortConfig.key === sortKey ? "active" : ""}`}
        />
        {sortConfig.key === sortKey && (
          <span className="sort-direction">
            {sortConfig.direction === "asc" ? (
              <ChevronUp size={14} />
            ) : (
              <ChevronDown size={14} />
            )}
          </span>
        )}
      </div>
    </th>
  );

  const getProgressColor = (progress) => {
    if (progress === 100) return "complete";
    if (progress >= 70) return "high";
    if (progress >= 40) return "medium";
    return "low";
  };

  const renderExpandedContent = (item) => {
    const isExpanded = expandedRows.has(item.id);
    if (!isExpanded) return null;

    return (
      <tr className="expanded-row">
        <td colSpan={isTeamView ? 9 : 8}>
          <div className="expanded-content">
            {/* Timeline Section */}
            <div className="expanded-section timeline-section">
              <h4>
                <Calendar size={16} />
                Timeline
              </h4>
              <div className="timeline-grid">
                {item.createdAt && (
                  <div className="timeline-item">
                    <span className="timeline-label">Created</span>
                    <span className="timeline-value">
                      {formatDateTime(item.createdAt)}
                    </span>
                  </div>
                )}
                {item.startedAt && (
                  <div className="timeline-item">
                    <span className="timeline-label">Started</span>
                    <span className="timeline-value">
                      {formatDateTime(item.startedAt)}
                    </span>
                  </div>
                )}
                {item.updatedAt && (
                  <div className="timeline-item">
                    <span className="timeline-label">Last Updated</span>
                    <span className="timeline-value">
                      {formatDateTime(item.updatedAt)}
                    </span>
                  </div>
                )}
                {item.dueAt && (
                  <div className="timeline-item">
                    <span className="timeline-label">Due Date</span>
                    <span className="timeline-value">
                      {formatDateTime(item.dueAt)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Checklist Section */}
            {item.checkListHistories && item.checkListHistories.length > 0 && (
              <div className="expanded-section checklist-section">
                <h4>
                  <ListChecks size={16} />
                  Checklist Items ({item.completedTasks}/{item.totalTasks})
                </h4>
                <div className="checklist-items">
                  {item.checkListHistories.map((task) => (
                    <div
                      key={task.id}
                      className={`checklist-item ${task.completed ? "completed" : ""}`}
                    >
                      <div className="task-header">
                        {task.completed ? (
                          <CheckCircle
                            size={16}
                            className="task-icon completed"
                          />
                        ) : (
                          <Clock size={16} className="task-icon pending" />
                        )}
                        <span className="task-name">{task.taskName}</span>
                      </div>
                      {task.remarks && (
                        <div className="task-remarks">{task.remarks}</div>
                      )}
                      {!task.completed && !task.isPlaceholder && (
                        <button
                          className="quick-complete-btn"
                          onClick={() =>
                            onQuickComplete(task.checkListId, item.id)
                          }
                          disabled={loadingItems.has(item.id)}
                        >
                          {loadingItems.has(item.id)
                            ? "Completing..."
                            : "Quick Complete"}
                        </button>
                      )}
                      {!task.completed && task.isPlaceholder && (
                        <button
                          className="quick-complete-btn"
                          onClick={() => onQuickComplete(task.id, item.id)}
                          disabled={loadingItems.has(item.id)}
                        >
                          {loadingItems.has(item.id)
                            ? "Completing..."
                            : "Quick Complete"}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Products Section */}
            {item.productHistories && item.productHistories.length > 0 && (
              <div className="expanded-section products-section">
                <h4>
                  <Package size={16} />
                  Products ({item.productHistories.length})
                </h4>
                <div className="products-grid">
                  {item.productHistories.map((product) => (
                    <div
                      key={product.id}
                      className="product-card"
                      onClick={() => onProductClick(product)}
                    >
                      <div className="product-header">
                        <span className="product-name">
                          {product.productName}
                        </span>
                        {product.skuCode && (
                          <span className="product-sku">{product.skuCode}</span>
                        )}
                      </div>
                      <div className="product-details">
                        <span className="product-quantity">
                          Qty: {product.quantity}
                        </span>
                        {product.price && (
                          <span className="product-price">
                            ₹{product.price.toLocaleString()}
                          </span>
                        )}
                      </div>
                      {product.specificationHistories &&
                        product.specificationHistories.length > 0 && (
                          <div className="product-specs">
                            {product.specificationHistories.map((spec) => (
                              <span key={spec.id} className="spec-badge">
                                {spec.specsName}: {spec.valueName}
                              </span>
                            ))}
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes Section */}
            {(item.notes || item.remarks) && (
              <div className="expanded-section notes-section">
                <h4>
                  <FileText size={16} />
                  Notes & Remarks
                </h4>
                {item.notes && (
                  <div className="note-item">
                    <strong>Notes:</strong>
                    <p>{item.notes}</p>
                  </div>
                )}
                {item.remarks && (
                  <div className="note-item">
                    <strong>Remarks:</strong>
                    <p>{item.remarks}</p>
                  </div>
                )}
              </div>
            )}

            {/* Actions Section */}
            <div className="expanded-section actions-section">
              <h4>
                <Target size={16} />
                Actions
              </h4>
              <div className="action-buttons">
                {isTeamView && onOpenBOM && (
                  <button
                    className="action-btn primary"
                    onClick={() => onOpenBOM(item.enquiryId)}
                  >
                    View BOM
                  </button>
                )}
                {onOpenMOM && (
                  <button
                    className="action-btn secondary"
                    onClick={() => onOpenMOM(item.enquiryId)}
                  >
                    View MOM
                  </button>
                )}
                {onProductEdit && (
                  <button
                    className="action-btn secondary"
                    onClick={() => onProductEdit(item)}
                  >
                    Edit Products
                  </button>
                )}
                {onAddProducts && (
                  <button
                    className="action-btn secondary"
                    onClick={() => onAddProducts(item)}
                  >
                    Add Products
                  </button>
                )}
                {canMoveToNextStage(item).canMove && (
                  <button
                    className="action-btn success"
                    onClick={() => onMoveToImmediateNext(item)}
                  >
                    <TrendingUp size={16} />
                    Move to Next Stage
                  </button>
                )}
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  if (!data || data.length === 0) {
    return (
      <div className="table-empty-state">
        <AlertCircle size={48} className="empty-icon" />
        <h3>No Enquiries Found</h3>
        <p>Try adjusting your filters to see results</p>
      </div>
    );
  }

  return (
    <div className="table-view-wrapper">
      <div className="table-info-bar">
        <div className="table-stats">
          <div className="stat-item">
            <FileText size={16} />
            <span>{data.length} Enquiries</span>
          </div>
          <div className="stat-item">
            <CheckCircle size={16} />
            <span>
              {
                data.filter(
                  (item) =>
                    item.status === "COMPLETED" ||
                    item.status === "COMPLETED_SUCCESSFULLY",
                ).length
              }{" "}
              Completed
            </span>
          </div>

          <div className="stat-item">
            <Clock size={16} />
            <span>
              {
                data.filter(
                  (item) =>
                    item.status === "PROCESSING" ||
                    item.status === "IN_PROGRESS",
                ).length
              }{" "}
              In Progress
            </span>
          </div>
        </div>
      </div>

      <div className="table-heading-column">
        <div className="search-bar-container">
          <div className="search-meta-controls">
            <span className="record-count">
              {startIndex + 1}-{Math.min(endIndex, sortedData.length)} of{" "}
              {sortedData.length} records
            </span>

            <div className="table-controls">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  className="search-input-table"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />

                {searchTerm && (
                  <button className="clear-search-btn" onClick={clearSearch}>
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div className="page-size-selector">
              <select
                value={pageSize}
                onChange={handlePageSizeChange}
                className="page-size-select"
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="table-container">
        <table className="enquiry-table">
          <thead>
            <tr>
              <th className="expand-column">
                <Eye size={16} />
              </th>
              <SortableHeader
                label="Stage"
                sortKey="stageName"
                icon={Activity}
              />
              <SortableHeader
                label="Client/Lead"
                sortKey="clientName"
                icon={Users}
              />
              <SortableHeader
                label="Products"
                sortKey="productCount"
                icon={Package}
              />
              <SortableHeader
                label="Progress"
                sortKey="progress"
                icon={TrendingUp}
              />
              {isTeamView && <th>Assigned To</th>}
              <SortableHeader
                label="Created"
                sortKey="createdAt"
                icon={Calendar}
              />
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item) => {
              const isExpanded = expandedRows.has(item.id);
              const canMove = canMoveToNextStage(item);

              return (
                <React.Fragment key={item.id}>
                  <tr
                    className={`table-row ${isExpanded ? "expanded" : ""} ${
                      selectedItem === item.id ? "selected" : ""
                    }`}
                  >
                    <td className="expand-cell">
                      <button
                        className="expand-btn"
                        onClick={() => toggleRowExpansion(item.id)}
                      >
                        {isExpanded ? (
                          <ChevronUp size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                      </button>
                    </td>

                    <td className="stage-cell">
                      <div className="stage-info">
                        {item.stageDetails?.stageType &&
                          getStageTypeIcon(item.stageDetails.stageType)}
                        <div className="stage-details">
                          <span className="stage-name">{item.stageName}</span>
                          {item.stageDetails?.sequenceNumber && (
                            <span className="stage-sequence">
                              Step {item.stageDetails.sequenceNumber}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="client-cell">
                      <div className="client-info">
                        <span className="client-name">
                          {item.leadName || item.clientName || "N/A"}

                          {item.leadName && (
                            <span className="client-badge new">New Lead</span>
                          )}
                        </span>
                        {(item.leadCode || item.clientCode) && (
                          <span className="client-code">
                            {item.leadCode || item.clientCode}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="products-cell">
                      <div className="products-summary">
                        <Package size={14} />
                        <span className="product-count">
                          {item.productHistories?.length || 0}
                        </span>
                        {item.productHistories &&
                          item.productHistories.length > 0 && (
                            <span className="total-qty">
                              (
                              {item.productHistories.reduce(
                                (sum, p) => sum + p.quantity,
                                0,
                              )}{" "}
                              qty)
                            </span>
                          )}
                      </div>
                    </td>

                    <td className="progress-cell">
                      <div className="progress-container">
                        <div className="progress-info">
                          <span className="progress-text">
                            {item.completedTasks}/{item.totalTasks}
                          </span>
                          <span className="progress-percentage">
                            {Math.round(item.progress)}%
                          </span>
                        </div>
                        <div className="progress-bar-wrapper">
                          <div
                            className={`progress-bar ${getProgressColor(item.progress)}`}
                            style={{ width: `${item.progress}%` }}
                          />
                        </div>
                      </div>
                    </td>

                    {isTeamView && (
                      <td className="assigned-cell">
                        <div className="assigned-info">
                          {item.assignedTo ? (
                            <>
                              <Users size={14} />
                              <span>{item.assignedTo}</span>
                            </>
                          ) : (
                            <span className="unassigned">Unassigned</span>
                          )}
                        </div>
                      </td>
                    )}

                    <td className="date-cell">
                      <div className="date-info">
                        {item.createdAt ? (
                          <>
                            <span className="date-value">
                              {new Date(item.createdAt).toLocaleDateString(
                                "en-IN",
                              )}
                            </span>
                            <span className="time-value">
                              {new Date(item.createdAt).toLocaleTimeString(
                                "en-IN",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </span>
                          </>
                        ) : (
                          <span className="no-date">N/A</span>
                        )}
                      </div>
                    </td>

                    <td className="actions-cell">
                      <div className="action-buttons-compact">
                        {!isChecklistComplete(item) && item.totalTasks > 0 && (
                          <button
                            className="table-action-btn checklist"
                            onClick={() => {
                              const firstIncomplete =
                                item.checkListHistories.find(
                                  (t) => !t.completed,
                                );
                              if (firstIncomplete) {
                                if (firstIncomplete.isPlaceholder) {
                                  onOpenModal(firstIncomplete.id, true);
                                } else {
                                  onOpenModal(
                                    firstIncomplete.checkListId,
                                    false,
                                  );
                                }
                              }
                            }}
                            title="Complete checklist"
                          >
                            <ListChecks size={16} />
                          </button>
                        )}
                        <button
                          className="table-action-btn stage"
                          onClick={() => onStageProgression(item)}
                          title={
                            canMove.canMove ? "Move to stage" : canMove.reason
                          }
                          disabled={!canMove.canMove}
                        >
                          <TrendingUp size={16} />
                        </button>

                        <EnquiryTimelineIcon
                          enquiryId={item.enquiryId}
                          getApiEndpoint={getApiEndpoint}
                        />
                      </div>
                    </td>
                  </tr>

                  {renderExpandedContent(item)}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {sortedData.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span className="pagination-text">
              Showing {startIndex + 1} to{" "}
              {Math.min(endIndex, sortedData.length)} of {sortedData.length}{" "}
              entries
            </span>
          </div>

          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-btn pagination-btn-nav"
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
              >
                «
              </button>

              <button
                className="pagination-btn pagination-btn-nav"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
              >
                ‹
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .slice(
                  Math.max(0, currentPage - 3),
                  Math.max(0, currentPage - 3) + 5,
                )
                .map((page) => (
                  <button
                    key={page}
                    className={`pagination-btn pagination-btn-number ${
                      page === currentPage ? "active" : ""
                    }`}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </button>
                ))}

              <button
                className="pagination-btn pagination-btn-nav"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                ›
              </button>

              <button
                className="pagination-btn pagination-btn-nav"
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                »
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TableView;
